import React, { useEffect, useMemo, useRef, useState } from "react";
import Select, { MultiValue } from "react-select";
import { getReq } from "../utils/request";
import { ActivityParticipant, ActivityPerson } from "../types/Activity";
import SelectField from "./record/SelectField";
import classes from "./contact/contactPicker.module.scss";

interface ActivityPeoplePickerProps {
    /** The record the activity is logged on — its own people are offered first. */
    parentType: string;
    parentId: number;
    value: ActivityParticipant[];
    /** `added` is the person just picked (with phone / email), null when someone was removed. */
    onChange: (participants: ActivityParticipant[], added: ActivityPerson | null) => void;
    id?: string;
}

type PersonOption = { value: number; label: string; person: ActivityPerson | null };

const GROUP_LABELS: Record<ActivityPerson["group"], string> = {
    record: "On this record",
    team: "Our team",
    other: "Other contacts",
};

const toParticipant = (person: ActivityPerson): ActivityParticipant => ({
    userDetailId: person.userDetailId,
    fullName: person.fullName,
    organizationName: person.organizationName,
    isStaff: person.group === "team",
});

/**
 * "With": the people an activity was with — the record's own people and our
 * team listed as soon as it opens, anyone else in CRX by typing a name.
 */
const ActivityPeoplePicker: React.FC<ActivityPeoplePickerProps> = ({ parentType, parentId, value, onChange, id = "activityParticipants" }) => {
    const [keyword, setKeyword] = useState("");
    const [people, setPeople] = useState<ActivityPerson[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    // Drops answers that arrive after a newer search started.
    const requestRef = useRef(0);

    useEffect(() => {
        const request = ++requestRef.current;
        const timer = setTimeout(async () => {
            setIsLoading(true);
            const query = `parentType=${encodeURIComponent(parentType)}&parentId=${parentId}${keyword.trim() ? `&keyword=${encodeURIComponent(keyword.trim())}` : ""}`;
            const response = await getReq(`activities/people?${query}`);
            if (request !== requestRef.current) return;
            setPeople(response.status === 200 && Array.isArray(response.body) ? response.body : []);
            setIsLoading(false);
        }, keyword ? 250 : 0);
        return () => clearTimeout(timer);
    }, [parentType, parentId, keyword]);

    const groups = useMemo(() => (Object.keys(GROUP_LABELS) as ActivityPerson["group"][])
        .map((group) => ({
            label: GROUP_LABELS[group],
            options: people
                .filter((person) => person.group === group)
                .map((person): PersonOption => ({ value: person.userDetailId, label: person.fullName, person })),
        }))
        .filter((group) => group.options.length > 0), [people]);

    const selected: PersonOption[] = value.map((participant) => ({
        value: participant.userDetailId,
        label: participant.fullName || "Unnamed contact",
        person: null,
    }));

    const handleChange = (options: MultiValue<PersonOption>) => {
        const known = new Map(value.map((participant) => [participant.userDetailId, participant]));
        const next = options.map((option) => known.get(option.value) ?? (option.person ? toParticipant(option.person) : { userDetailId: option.value, fullName: option.label }));
        const added = options.find((option) => !known.has(option.value))?.person ?? null;
        onChange(next, added);
    };

    const formatOptionLabel = (option: PersonOption, meta: { context: string }) => {
        if (meta.context !== "menu" || !option.person) return <span>{option.label}</span>;
        const detail = [option.person.position, option.person.organizationName].filter(Boolean).join(" · ");
        return (
            <div className={classes.optionRow}>
                <div className={classes.optionMain}>
                    <span className={classes.optionName}>{option.label}</span>
                    {detail && <span className={classes.optionRef}>{detail}</span>}
                </div>
            </div>
        );
    };

    return (
        <SelectField
            label="With"
            hasValue={value.length > 0}
            tooltip="The people it was with: this record's people, our team, or anyone in CRX by name. It shows on each person's contact page, and on our team's calendars."
        >
            <Select<PersonOption, true>
                inputId={id}
                isMulti
                value={selected}
                options={groups}
                inputValue={keyword}
                onInputChange={(text, meta) => {
                    if (meta.action === "input-change") setKeyword(text);
                    else if (meta.action === "menu-close" || meta.action === "input-blur") setKeyword("");
                }}
                onChange={handleChange}
                isLoading={isLoading}
                formatOptionLabel={formatOptionLabel}
                placeholder="With — pick people"
                aria-label="With"
                noOptionsMessage={() => (keyword.trim() ? "No one by that name" : "Type a name to find someone")}
                closeMenuOnSelect={false}
                className="custom-select"
                classNamePrefix="custom-select"
            />
        </SelectField>
    );
};

export default ActivityPeoplePicker;
